import React, { useEffect, useState } from 'react';
import { DialogBox } from '../../dialog-box/dialog-box';
import { Organization } from '../../../models/interfaces/organization';
import { OrganizationSubscription } from '../../../models/interfaces/organization-subscription';
import OrganizationService from '../../../utilities/services/organization-service';
import OrganizationSubscriptionService from '../../../utilities/services/organization-subscription-service';
import { OrganizationStatus } from '../../../models/enumerations/organization-status';
import { Grid } from '@mui/material'
import { ViewOrganizationField } from './view-organization-field';
import { PlanType } from '../../../models/enumerations/plan-type';
import moment from 'moment';
import { PaymentType } from '../../../models/enumerations/payment-type';
import { User } from '../../../models/interfaces/user';
import UserService from '../../../utilities/services/user-service';
import OrganizationUtil from '../../../utilities/organization-util';
import { OrganizationStatusBadge } from '../../organization-status-badge/organization-status-badge';
import { OrganizationType } from '../../../models/enumerations/organization-type';
import { Button } from '../../button/button';
import { useTranslation } from 'react-i18next';
import { PlanTypeLabels } from '../../../models/plan-type';
import { LanguageDateFormat } from '../../../models/language-date-format';
import { IndustryEnglishLabel } from '../../../models/industry';
import { PaymentCycleLabel } from '../../../models/payment-cycle';
import ExternalOrgService from '../../../utilities/services/external-org-service';
import { enqueueSnackbar } from 'notistack';
import { createLogger } from '../../../utilities/logger';

const log = createLogger("ViewOrganizationModal");

type ViewOrganizationModalProps = {
    organizationId: string | undefined,
    open: boolean,
    onClose: Function,
    onEdit: Function,
};

export const ViewOrganizationModal: React.FC<ViewOrganizationModalProps> = (props: ViewOrganizationModalProps) => {
    const {
        organizationId,
        open,
        onClose,
        onEdit,
    } = props;

    const [organization, setOrganization] = useState<Organization>()
    const [subscription, setSubscription] = useState<OrganizationSubscription>();
    const [trial, setTrial] = useState<boolean>(false);
    const [accountHolder, setAccountHolder] = useState<User>();
    const [userQuantity, setUserQuantity] = useState<number>();
    const [expired, setExpired] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>(true);
    const [accountManagerName, setAccountManagerName] = useState("");
    const [apiKeyMasked, setApiKeyMasked] = useState<string>("");
    const [apiKeyLoading, setApiKeyLoading] = useState<boolean>(false);
    const { t, i18n } = useTranslation();
    const reset = () => {
        setOrganization(undefined);
        setSubscription(undefined);
        setTrial(false);
        setAccountHolder(undefined);
        setUserQuantity(undefined);
        setExpired(undefined);
    }

    useEffect(() => {
        const loadData = async () => {
            if (!organizationId) return;
            try {
                const o = await OrganizationService.get(organizationId);
                if (!o) {
                    log.warn("Organization details not found", {
                        organizationId,
                    });
                    return;
                }

                setOrganization(o);

                const isSub = OrganizationUtil.isSubOrganization(o);

                // Sub-organizations have no billing of their own; their account
                // type (trial vs paid) matches the head organization's.
                const subscriptionOrgId = isSub && o.parentOrganizationId ? o.parentOrganizationId : organizationId;

                const subs = await OrganizationSubscriptionService.getByOrganizationId(subscriptionOrgId);
                const orgSub = subs.length > 0 ? subs[0] : undefined;

                if (orgSub?.accountType.value === OrganizationType.Trial) {
                    setTrial(true);
                }
                if (o.accountHolderId) {
                    const holder = await UserService.get(o.accountHolderId);
                    if (holder) setAccountHolder(holder);
                }

                const uQuantity = await OrganizationUtil.getOrganizationUserCount(o);
                setUserQuantity(uQuantity);

                // Only the org's own subscription is bound to the view;
                // sub-organizations inherit the account type but carry no
                // subscription.
                if (!isSub && orgSub) {
                    setSubscription(orgSub);
                    if (orgSub.renewDate) {
                        const renewDate = moment(orgSub.renewDate);
                        const exp = renewDate.isSameOrBefore(moment());
                        setExpired(exp);
                    }
                }

                setAccountManagerName(o.accountManager?.label || "");

                setLoading(false);
            } catch (e) {
                log.error("Failed to load organization details", {
                    exception: e,
                    organizationId,
                });
                setLoading(false);
            }
        }
        reset();
        loadData();
    }, [organizationId, open]);

    const handleCopyApiKey = async () => {
        if (!organizationId) return;
        try {
            const response = await ExternalOrgService.revealKey(organizationId);
            if (response?.key) {
                await navigator.clipboard.writeText(response.key);
                enqueueSnackbar(t('organization.edit.fields.keyCopied'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
            }
        } catch {
            enqueueSnackbar(t('organization.edit.fields.keyCopyFailed'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
        }
    };

    useEffect(() => {
        const loadApiKey = async () => {
            if (!organizationId || !organization) return;
            if (organization.plan?.value !== PlanType.External) return;
            setApiKeyLoading(true);
            try {
                const keyInfo = await ExternalOrgService.getKey(organizationId);
                if (keyInfo?.maskedKey) {
                    setApiKeyMasked(keyInfo.maskedKey);
                }
            } catch {
                // Key may not exist yet
            }
            setApiKeyLoading(false);
        };
        loadApiKey();
    }, [organizationId, organization]);

    const getAccountHolderName = () => {
        if (!accountHolder) return 'none';

        if (accountHolder.firstName && accountHolder.lastName) {
            return `${accountHolder.firstName} ${accountHolder.lastName}`;
        }

        if (accountHolder.email) {
            return accountHolder.email;
        }

        return `id: ${accountHolder.id}`
    }

    // const paidOrTrial = (status: OrganizationStatus) => {
    //     if (status === OrganizationStatus.Trial) {
    //         return 'Trial';
    //     }
    //     return 'Paid';
    // };

    const getOrganizationStatus = () => {
        if (!organization) {
            return OrganizationStatus.Active;
        }

        const status = organization.status;

        if (typeof status === 'string') {
            if (status === OrganizationStatus.Deactivated.toString() || organization.active === false) {
                return OrganizationStatus.Deactivated;
            }
            if (status === OrganizationStatus.Active.toString()) {
                return OrganizationStatus.Active;
            }
            if (status === OrganizationStatus.Expired.toString()) {
                return OrganizationStatus.Expired;
            }
        } else {
            if (status === OrganizationStatus.Deactivated || organization.active === false) {
                return OrganizationStatus.Deactivated
            }
            if (status === OrganizationStatus.Active) {
                return OrganizationStatus.Active;
            }
            if (status === OrganizationStatus.Expired) {
                return OrganizationStatus.Expired;
            }
        }

        return OrganizationStatus.Active;
    }

    const getOrganizationLimit = () => {
        if (organization?.userLimit) {
            return organization.userLimit;
        }

        if (subscription?.userLimit) {
            return subscription.userLimit;
        }

        return 'no limit';
    }

    // Limit fields read from the subscription for head/regular orgs (the
    // purchased total); sub-organizations carry no subscription, so their
    // limit is the allocated slice stored on the organization document.
    const getUserLimit = () => {
        if (OrganizationUtil.isSubOrganization(organization)) {
            return organization?.userLimit ?? '';
        }

        return subscription?.userLimit ?? '';
    }

    const getWellnessLimit = () => {
        if (OrganizationUtil.isSubOrganization(organization)) {
            return organization?.mySymmioLicense ?? '';
        }

        return subscription?.mySymmioLicense ?? '';
    }

    const handleEdit = () => {
        onEdit(organizationId);
    }
    return (
        <>
            <DialogBox
                maxWidth="lg"
                fullWidth
                open={!!organizationId && open}
                onClose={onClose}
                sx={{ maxWidth: '1000px', height: '1200px', margin: 'auto', maxHeight: '99ch' }}
            >
                <div style={{ marginTop: '30px' }} />
                <OrganizationStatusBadge status={getOrganizationStatus()} expired={expired} />
                <h1 style={{ margin: '16px 0px 32px 0px' }}>{organization ? organization.name : ''}</h1>
                <Grid container spacing={0}>

                    {/* ===== FIRST ROW ===== */}


                    <ViewOrganizationField
                        firstRow
                        label={t('organization.edit.fields.plan')}
                        value={subscription?.plan?.value !== undefined ? PlanTypeLabels[subscription.plan.value as PlanType] : undefined}
                        loading={loading}
                    />
                    {(accountManagerName) ?
                        (<ViewOrganizationField
                            firstRow
                            label={t('organization.edit.fields.accountManager')}
                            value={accountManagerName}
                            loading={loading}
                        />) :
                        (
                            <ViewOrganizationField />
                        )
                    }
                    <ViewOrganizationField
                        firstRow
                        label={t('organization.edit.fields.pricingModel')}
                        value={subscription?.paymentType?.label ?? 'n/a'}
                        hidden={subscription?.plan?.value !== PlanType.Enterprise}
                        loading={loading}
                    />
                    <ViewOrganizationField
                        firstRow
                        rowEnd
                        label={trial ? t('organization.edit.fields.expirationDate') : t('organization.edit.fields.renewDate')}
                        value={subscription?.startDate && subscription.renewDate ? moment(subscription.renewDate).format(LanguageDateFormat[i18n.language]) : 'n/a'}
                        loading={loading}
                        expired={expired}
                    />

                    {/* ===== SECOND ROW ===== */}

                    <ViewOrganizationField
                        label={t('organization.edit.fields.userLimit')}
                        value={getUserLimit()}
                        loading={loading}
                    />

                    <ViewOrganizationField
                        label={t('organization.edit.fields.wellnessLimit')}
                        value={getWellnessLimit()}
                        loading={loading}
                    />
                    {/* {
                        trial ? (<ViewOrganizationField />)
                            : (
                                <ViewOrganizationField
                                    label={t('organization.edit.fields.paymentCycle')}
                                    value={subscription?.paymentCycle ? PaymentCycleLabel[subscription?.paymentCycle?.value as PaymentCycle] : ''}
                                    loading={loading}
                                    hidden={trial}
                                />
                            )
                    } */}
                    {
                        subscription
                            && subscription.plan?.value === PlanType.Enterprise ? (
                            subscription.paymentType?.value === PaymentType.PayPerUser ?
                                <ViewOrganizationField
                                    label={t('organization.edit.fields.pricePerUser')}
                                    value={subscription?.pricePerUser ?? 'none'}
                                    startAdornment={subscription?.pricePerUser ? '$' : ''}
                                    loading={loading}
                                />
                                :
                                <ViewOrganizationField
                                    label={t('organization.edit.fields.price')}
                                    value={subscription?.price ?? 'none'}
                                    startAdornment={subscription?.price ? '$' : ''}
                                    loading={loading}
                                    hidden={trial}
                                />
                        )
                            :
                            <ViewOrganizationField />
                    }

                    <ViewOrganizationField
                        rowEnd
                        label={t('organization.edit.fields.userQuantity')}
                        userQuantity={userQuantity}
                        value={` / ${getOrganizationLimit()}`}
                        loading={loading}
                    />



                    {/* ===== THIRD ROW ===== */}

                    <ViewOrganizationField
                        label={t('organization.edit.fields.accountHolder')}
                        value={getAccountHolderName()}
                        loading={loading}
                        columns={2}
                    />

                    {
                        subscription?.plan?.value === PlanType.Enterprise || subscription?.plan?.value === PlanType.EnterpriseTrial ? (
                            <ViewOrganizationField
                                label={t('organization.edit.fields.organizationLimit')}
                                value={organization?.organizationLimit}
                            />
                        ) : (
                            <ViewOrganizationField />
                        )
                    }


                    <ViewOrganizationField
                        rowEnd
                        label={t('organization.edit.fields.programsQuantity')}
                        userQuantity={organization?.mySymmioLicenseCount}
                        value={` / ${organization?.mySymmioLicense}`}
                        loading={loading}
                    />

                    {/* ===== FOURTH ROW ===== */}

                    <ViewOrganizationField
                        label={t('organization.edit.fields.industry')}
                        value={organization?.industry ? IndustryEnglishLabel[organization?.industry.value] : 'n/a'}
                        loading={loading}
                        columns={2}
                    />

                    {/* ===== EXTERNAL ROW ===== */}
                    {organization?.plan?.value === PlanType.External && (
                        <Grid container spacing={0}>
                            <Grid
                                item
                                xs={12} sm={6}
                                sx={{ paddingTop: '40px' }}
                            >
                                <p style={{ fontSize: '12px', fontWeight: '700', paddingBottom: '8px', wordWrap: 'break-word' }}>
                                    {t('organization.edit.fields.apiKey')}
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <p style={{ fontSize: '16px', fontWeight: '300', wordWrap: 'break-word' }}>
                                        {apiKeyLoading ? '' : (apiKeyMasked || 'No key generated')}
                                    </p>
                                    <Button
                                        buttonStyle='primary'
                                        buttonText={t('organization.edit.fields.copyKey')}
                                        onClick={handleCopyApiKey}
                                        disabled={!apiKeyMasked || apiKeyLoading}
                                    />
                                </div>
                            </Grid>
                            <ViewOrganizationField
                                label={t('organization.edit.fields.billingPeriod')}
                                value={subscription?.paymentCycle ? PaymentCycleLabel[subscription.paymentCycle.value] : 'Not set'}
                                loading={loading}
                            />
                            <ViewOrganizationField
                                rowEnd
                                label={t('organization.edit.fields.billingStartDate')}
                                value={subscription?.billingStartDate ? moment(subscription.billingStartDate).format(LanguageDateFormat[i18n.language]) : 'Not set'}
                                loading={loading}
                            />
                        </Grid>
                    )}

                </Grid>
                <div style={{ marginTop: '65px', display: 'flex', justifyContent: 'right' }}>
                    <Button
                        disabled={organization?.plan?.value === PlanType.Partner}
                        buttonStyle='primary'
                        onClick={handleEdit}
                        buttonText={t('buttons.btn_edit')} />
                </div>
                <div style={{ marginBottom: '30px' }} />
            </DialogBox >
        </>
    );
}
